import React, { useEffect, useRef } from "react";

import "./LandingPage.css";

const LandingPage = () => {
  const headerRef = useRef(null);

  useEffect(() => {
    // ── Mobile nav toggle ──
    const hamburger = document.getElementById("hamburger");
    const navLinks = document.querySelector(".nav-links");

    const handleHamburger = () => navLinks?.classList.toggle("open");
    const handleNavLink = () => navLinks?.classList.remove("open");

    hamburger?.addEventListener("click", handleHamburger);
    navLinks
      ?.querySelectorAll("a")
      .forEach((link) => link.addEventListener("click", handleNavLink));

    // ── Scroll: elevate header ──
    const handleScroll = () => {
      if (headerRef.current) {
        headerRef.current.style.boxShadow =
          window.scrollY > 10 ? "0 4px 24px rgba(10,31,68,.12)" : "none";
      }
    };
    window.addEventListener("scroll", handleScroll);

    // ── IntersectionObserver: fade-in cards ──
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.style.opacity = "1";
            e.target.style.transform = "translateY(0)";
          }
        }),
      { threshold: 0.12 },
    );
    document
      .querySelectorAll(".feature-card, .pricing-card, .testimonial-card")
      .forEach((el) => {
        el.style.opacity = "0";
        el.style.transform = "translateY(24px)";
        el.style.transition = "opacity .5s ease, transform .5s ease";
        observer.observe(el);
      });

    // ── Cleanup ──
    return () => {
      hamburger?.removeEventListener("click", handleHamburger);
      navLinks
        ?.querySelectorAll("a")
        .forEach((link) => link.removeEventListener("click", handleNavLink));
      window.removeEventListener("scroll", handleScroll);
      observer.disconnect();
    };
  }, []);

  return (
    <>
      {/* ═══════════════════════ HEADER ═══════════════════════ */}
      <header className="header" ref={headerRef}>
        <div className="container">
          <nav className="navbar">
            <div className="logo">
              <div className="logo-icon">
                <img
                  src="assets_landing_page/WhatsApp_Image_2026-05-30_at_5.53.18_PM__1_-removebg-preview.png"
                  alt="PULSE logo"
                />
              </div>
              <h1>PULSE</h1>
            </div>

            <ul className="nav-links">
              <li>
                <a href="#features">Features</a>
              </li>
              <li>
                <a href="#ecosystem">Ecosystem</a>
              </li>
              <li>
                <a href="#about">About</a>
              </li>
              <li>
                <a href="#testimonials">Testimonials</a>
              </li>
              <li>
                <a href="#pricing">Pricing</a>
              </li>
              <li>
                <a href="#contact">Contact</a>
              </li>
            </ul>

            <div className="cta-buttons desktop-cta">
              <a href="/user-login" className="btn btn-outline">
                Log In
              </a>
              <a href="#contact" className="btn btn-primary">
                Get Started
              </a>
            </div>

            <div className="hamburger" id="hamburger" aria-label="Toggle menu">
              <span className="bar"></span>
              <span className="bar"></span>
              <span className="bar"></span>
            </div>
          </nav>
        </div>
      </header>

      {/* ═══════════════════════ HERO ═══════════════════════ */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">
              <i className="fas fa-star"></i>
              Trusted by clinics &amp; labs across Assam
            </div>
            <h1>
              Streamline Your
              <br />
              <span className="highlight">Clinic Operations</span>
              <br />
              with PULSE
            </h1>
            <p>
              The complete Lab and Clinic Management System designed to simplify
              patient care, improve efficiency, and boost revenue — all from one
              powerful dashboard.
            </p>
            <div className="cta-buttons">
              <a href="#contact" className="btn btn-primary btn-lg">
                <i className="fas fa-calendar-check"></i> Request a Demo
              </a>
              <a href="#features" className="btn btn-outline btn-lg">
                Explore Features
              </a>
            </div>
            <div className="hero-stats">
              <div className="stat-item">
                <span className="stat-number">100+</span>
                <span className="stat-label">Active Clinics</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">100K+</span>
                <span className="stat-label">Reports Generated</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">40%</span>
                <span className="stat-label">Less Admin Work</span>
              </div>
            </div>
          </div>

          <div className="hero-image">
            <img
              src="/assets_landing_page/hero-dashboard.png"
              alt="PULSE Dashboard"
            />
            <div className="hero-float-badge">
              <div className="float-icon">
                <i className="fas fa-shield-alt"></i>
              </div>
              <div className="float-text">
                <strong>Secure &amp; Reliable</strong>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════ FEATURES ═══════════════════════ */}
      <section id="features" className="features">
        <div className="container">
          <div className="section-header">
            <h2>Comprehensive Solutions for Modern Healthcare</h2>
            <p>
              Everything you need to run your clinic or laboratory efficiently
              in one powerful platform.
            </p>
          </div>
          <div className="features-grid">
            {[
              {
                icon: "fa-user-injured",
                title: "Patient Management",
                desc: "Streamline patient registration, maintain complete medical histories, and automate appointment scheduling.",
              },
              {
                icon: "fa-user-md",
                title: "Doctor Management",
                desc: "Manage doctor schedules, specialties, and availability to optimize your clinic's workflow.",
              },
              {
                icon: "fa-file-invoice-dollar",
                title: "Billing System",
                desc: "Generate invoices, track payments, and manage expense claims seamlessly.",
              },
              {
                icon: "fa-file-medical-alt",
                title: "Lab Reports",
                desc: "Create, manage, and share professional lab reports with patients and doctors instantly.",
              },
              {
                icon: "fa-chart-line",
                title: "Analytics Dashboard",
                desc: "Gain insights into your clinic's performance with detailed reports and analytics.",
              },
              {
                icon: "fa-mobile-alt",
                title: "Mobile Access",
                desc: "Access your clinic data anytime, anywhere from any device with our mobile-responsive design.",
              },
            ].map((f) => (
              <div className="feature-card" key={f.title}>
                <div className="eco-icon">
                  <i className={`fas ${f.icon}`}></i>
                </div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════ ECOSYSTEM ═══════════════════════ */}
      <section id="ecosystem" className="ecosystem">
        <div className="container">
          <div className="section-header">
            <div className="section-rule-title">
              <span className="rule-line"></span>
              <h2>Core Ecosystem Features</h2>
              <span className="rule-line"></span>
            </div>
          </div>

          <div className="ecosystem-grid">
            {/* Card 1 */}
            <div className="ecosystem-card">
              <div className="ecosystem-icon-row">
                <div className="eco-icon">
                  <i className="fas fa-id-card"></i>
                </div>
                <div className="eco-icon">
                  <i className="fas fa-chart-bar"></i>
                </div>
              </div>
              <h3>
                <span className="eco-num">1. UNIFIED PATIENT EMR</span>{" "}
                <span className="eco-sub">(Electronic Medical Records)</span>
              </h3>
              <p>
                Experience a 360-degree view of patient health. Pulse ensures
                that data is intelligently organized.
              </p>
              <ul className="eco-list">
                <li>
                  <strong>Digital Health Identities:</strong> Centralized
                  patient profiles with comprehensive visit histories.
                </li>
                <li>
                  <strong>Real-time Results:</strong> Immediate access to lab
                  reports and diagnostic trends.
                </li>
                <li>
                  <strong>Interoperability:</strong> Securely share records
                  across departments or with referring physicians.
                </li>
              </ul>
            </div>

            {/* Card 2 */}
            <div className="ecosystem-card">
              <div className="ecosystem-icon-row">
                <div className="eco-icon">
                  <i className="fas fa-chart-line"></i>
                </div>
                <div className="eco-icon">
                  <i className="fas fa-coins"></i>
                </div>
              </div>
              <h3>
                <span className="eco-num">
                  2. COMPREHENSIVE FINANCIAL SUITE
                </span>{" "}
                <span className="eco-sub">(Accounts)</span>
              </h3>
              <p>
                Turn your diagnostic center into a high-performance business
                with integrated accounting.
              </p>
              <ul className="eco-list">
                <li>
                  <strong>Smart Invoicing:</strong> Automated billing for tests,
                  packages, and corporate tie-ups.
                </li>
                <li>
                  <strong>Expense Tracking:</strong> Monitor lab consumables and
                  overheads in real-time.
                </li>
                <li>
                  <strong>Revenue Analytics:</strong> Detailed dashboards to
                  track daily collections and outstanding payments.
                </li>
              </ul>
            </div>

            {/* Card 3 */}
            <div className="ecosystem-card">
              <div className="ecosystem-icon-row">
                <div className="eco-icon">
                  <i className="fas fa-clipboard-list"></i>
                </div>
                <div className="eco-icon">
                  <i className="fas fa-search"></i>
                </div>
              </div>
              <h3>
                <span className="eco-num">3. TRANSPARENT GOVERNANCE</span>{" "}
                <span className="eco-sub">(Audit &amp; Compliance)</span>
              </h3>
              <p>
                Maintain the highest standards of integrity with built-in audit
                trails.
              </p>
              <ul className="eco-list">
                <li>
                  <strong>Activity Logs:</strong> Every modification is
                  timestamped and linked to a specific user.
                </li>
                <li>
                  <strong>Compliance Ready:</strong> Designed to meet data
                  protection standards and clinical accreditation requirements.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════ ADVANTAGE ═══════════════════════ */}
      <section className="advantage">
        <div className="container">
          <div className="section-header">
            <div className="section-rule-title">
              <span className="rule-line"></span>
              <h2>The Pulse Advantage: Beyond Software</h2>
              <span className="rule-line"></span>
            </div>
            <blockquote className="advantage-quote">
              "We don't just provide a platform; we provide a partnership that
              grows with your laboratory."
            </blockquote>
          </div>

          <div className="advantage-grid">
            <div className="advantage-card">
              <div className="advantage-icon">
                <i className="fas fa-headset"></i>
                <span className="adv-badge">24/7</span>
              </div>
              <h3>Continuous Online Support</h3>
              <p>
                Our expert technical team is available around the clock to
                ensure your lab never experiences a second of downtime.
              </p>
            </div>
            <div className="advantage-card">
              <div className="advantage-icon">
                <i className="fas fa-cogs"></i>
              </div>
              <h3>Quick Customizations</h3>
              <p>
                Every laboratory has a unique workflow. Pulse is built on a
                modular architecture, allowing us to tailor screens, reports,
                and logic to your specific needs within record time.
              </p>
            </div>
            <div className="advantage-card">
              <div className="advantage-icon">
                <i className="fas fa-calendar-alt"></i>
              </div>
              <h3>Evolving Features on a Monthly Basis</h3>
              <p>
                The diagnostic world moves fast, and so do we. Pulse subscribers
                receive automatic monthly updates — new tools and optimized
                workflows — ensuring you stay ahead of the curve without
                additional costs.
              </p>
            </div>
          </div>

          <div className="advantage-cta">
            <h3>Ready to Elevate Your Diagnostic Standards?</h3>
            <p>
              Contact us today for a live demo and see how Pulse can transform
              your operations.
            </p>
            <div
              className="cta-buttons"
              style={{ justifyContent: "center", marginTop: "24px" }}
            >
              <a href="#contact" className="btn btn-primary btn-lg">
                <i className="fas fa-calendar-check"></i> Book a Live Demo
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════ DASHBOARD PREVIEW ═══════════════════════ */}
      <section className="dashboard-preview">
        <div className="container">
          <div className="section-header">
            <h2>Powerful and Intuitive Interface</h2>
            <p>
              Crafted with precision to meet real-world clinical needs —
              intuitive for every user.
            </p>
          </div>
          <div className="dashboard-image">
            <img
              src="/assets_landing_page/dashboard.png"
              alt="PULSE Dashboard Preview"
            />
          </div>
          <div className="dashboard-features">
            {[
              {
                num: "01",
                title: "User-Friendly Dashboard",
                desc: "Intuitive interface that requires minimal training to master.",
              },
              {
                num: "02",
                title: "Comprehensive Patient Records",
                desc: "All patient information in one place for quick and easy access.",
              },
              {
                num: "03",
                title: "Integrated Appointment System",
                desc: "Manage appointments, send reminders, and reduce no-shows.",
              },
            ].map((f) => (
              <div className="dashboard-feature" key={f.num}>
                <div className="feature-number">{f.num}</div>
                <div className="feature-text">
                  <h3>{f.title}</h3>
                  <p>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════ TESTIMONIALS ═══════════════════════ */}
      <section id="testimonials" className="testimonials">
        <div className="container">
          <div className="section-header">
            <h2>Trusted by Healthcare Professionals</h2>
            <p>See what our clients have to say about PULSE</p>
          </div>
          <div className="testimonial-grid">
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>
                  "PULSE has revolutionized how we manage our clinic. The
                  patient management system has reduced our administrative work
                  by 40%."
                </p>
              </div>
              <div className="testimonial-author">
                <img
                  src="https://res.cloudinary.com/dotuhpjuq/image/upload/v1738647135/organization_logos/igwvnssrrlujapyuv907.jpg"
                  alt="Dr. Nirupam Kalita"
                />
                <div className="author-info">
                  <h4>Dr. Nirupam Kalita</h4>
                  <p>Dermatologist, Prapti Health and Diagnostics, Boko</p>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>
                  "The billing system in PULSE has streamlined our financial
                  operations and improved our cash flow significantly."
                </p>
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">RS</div>
                <div className="author-info">
                  <h4>Dr. R.S Rahman</h4>
                  <p>Surgeon, Allied Clinic, Durga Mandir, Goalpara</p>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>
                  "As a laboratory owner, the lab report management in PULSE has
                  been a game-changer for our workflow efficiency."
                </p>
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">MN</div>
                <div className="author-info">
                  <h4>Mahesh Nath</h4>
                  <p>
                    Owner &amp; Head Lab Technician, Arogya Clinical Laboratory,
                    Dudhnoi
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════ PRICING ═══════════════════════ */}
      <section id="pricing" className="pricing">
        <div className="container">
          <div className="section-header">
            <h2>Flexible &amp; Transparent Pricing</h2>
            <p className="subtitle">
              Tailored plans for every department. Bundle all for best value.
            </p>
          </div>

          {/* Legend */}
          <div className="pricing-legend">
            <div className="legend-pill">
              <span className="legend-dot onboard"></span>
              <span>
                <strong>Onboard</strong> — First-time sale &amp; training
              </span>
            </div>
            <div className="legend-pill">
              <span className="legend-dot arc"></span>
              <span>
                <strong>ARC</strong> — Annual Renewal Contract
              </span>
            </div>
          </div>

          {/* Main cards */}
          <div className="pricing-main-grid">
            <div className="pricing-main-card">
              <div className="pmc-badge">Most Popular</div>
              <div className="pmc-icon">
                <i className="fas fa-microscope"></i>
              </div>
              <h3>Pathology / Radiology</h3>
              <p className="pmc-sub">Dedicated lab reporting suite</p>
              <div className="pmc-prices">
                <div className="pmc-price-row">
                  <span className="pmc-label onboard-label">Onboard</span>
                  <span className="pmc-amount">₹8,000</span>
                </div>
                <div className="pmc-price-row">
                  <span className="pmc-label arc-label">ARC</span>
                  <span className="pmc-amount arc-amount">
                    ₹5,000<span className="pmc-year">/yr</span>
                  </span>
                </div>
              </div>
              <ul className="pmc-features">
                <li>
                  Pathology <strong>or</strong> Radiology module
                </li>
                <li>Report generation upto 15/day</li>
                <li>2 default users included</li>
                <li>Report design &amp; data migration</li>
                <li>Training included</li>
              </ul>
              <a href="#contact" className="btn btn-primary">
                Get Started
              </a>
            </div>

            <div className="pricing-main-card featured-main">
              <div className="pmc-badge popular">Best Value</div>
              <div className="pmc-icon gradient-icon">
                <i className="fas fa-hospital"></i>
              </div>
              <h3>OPD + Pathology / Radiology</h3>
              <p className="pmc-sub">Complete clinic &amp; lab combo</p>
              <div className="pmc-prices">
                <div className="pmc-price-row">
                  <span className="pmc-label onboard-label">Onboard</span>
                  <span className="pmc-amount">₹10,000</span>
                </div>
                <div className="pmc-price-row">
                  <span className="pmc-label arc-label">ARC</span>
                  <span className="pmc-amount arc-amount">
                    ₹6,000<span className="pmc-year">/yr</span>
                  </span>
                </div>
              </div>
              <ul className="pmc-features">
                <li>OPD management module</li>
                <li>
                  Pathology <strong>or</strong> Radiology module
                </li>
                <li>3 default users included</li>
                <li>Report generation upto 15/day across all</li>
                <li>Priority onboarding &amp; training</li>
              </ul>
              <a href="#contact" className="btn btn-primary">
                Get Started
              </a>
            </div>

            <div className="pricing-main-card">
              <div className="pmc-badge enterprise">Enterprise</div>
              <div className="pmc-icon">
                <i className="fas fa-layer-group"></i>
              </div>
              <h3>OPD, Pathology &amp; Radiology</h3>
              <p className="pmc-sub">Full-featured diagnostic centre</p>
              <div className="pmc-prices">
                <div className="pmc-price-row">
                  <span className="pmc-label onboard-label">Onboard</span>
                  <span className="pmc-amount">₹12,000</span>
                </div>
                <div className="pmc-price-row">
                  <span className="pmc-label arc-label">ARC</span>
                  <span className="pmc-amount arc-amount">
                    ₹7,200<span className="pmc-year">/yr</span>
                  </span>
                </div>
              </div>
              <ul className="pmc-features">
                <li>OPD + Pathology + Radiology</li>
                <li>4 default users included</li>
                <li>Report generation upto 15/day across all</li>
                <li>Full data migration &amp; training</li>
                <li>24/7 priority support</li>
              </ul>
              <a href="#contact" className="btn btn-primary">
                Get Started
              </a>
            </div>
          </div>

          {/* Mini add-on cards */}
          <div className="pricing-mini-header">
            <span className="rule-line"></span>
            <span className="mini-header-text">Add-ons &amp; Entry Plans</span>
            <span className="rule-line"></span>
          </div>

          <div className="pricing-mini-grid">
            {[
              {
                icon: "fa-stethoscope",
                title: "OPD Only",
                desc: "Outpatient department management",
                onboard: "₹3,000",
                arc: "₹1,800",
                users: "2 users",
              },
              {
                icon: "fa-flask",
                title: "Microbiology Add-on",
                desc: "Extend any plan with micro reporting",
                onboard: "₹2,000",
                arc: "₹1,200",
                users: "1 user",
              },
              {
                icon: "fa-user-plus",
                title: "Extra User",
                desc: "Add additional users to any plan",
                onboard: "₹500",
                arc: "₹300",
                users: "per user",
              },
            ].map((c) => (
              <div className="pricing-mini-card" key={c.title}>
                <div className="mini-icon">
                  <i className={`fas ${c.icon}`}></i>
                </div>
                <div className="mini-body">
                  <h4>{c.title}</h4>
                  <p>{c.desc}</p>
                </div>
                <div className="mini-prices">
                  <div className="mini-price-row">
                    <span className="mini-label onboard-label">Onboard</span>
                    <span className="mini-amount">{c.onboard}</span>
                  </div>
                  <div className="mini-price-row">
                    <span className="mini-label arc-label">ARC</span>
                    <span className="mini-amount">
                      {c.arc}
                      <small>/yr</small>
                    </span>
                  </div>
                  <div className="mini-users">
                    <i className="fas fa-user"></i> {c.users}
                  </div>
                </div>
                {/* <a href="#contact" className="btn btn-outline mini-btn">
                  Add
                </a> */}
              </div>
            ))}
          </div>
        </div>

        <p
          style={{
            textAlign: "center",
            marginTop: "28px",
            fontSize: "0.83rem",
            color: "#5a6a8a",
            padding: "0 24px",
          }}
        >
          * For Unlimited Reporting ARC will be same as Onbording fees
        </p>
      </section>

      {/* ═══════════════════════ ABOUT ═══════════════════════ */}
      <section id="about" className="about">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2>About PULSE</h2>
              <span className="tagline">
                Simplifying healthcare operations through powerful software.
              </span>
              <p>
                PULSE (Lab &amp; Clinic Management System) is a next-generation
                platform built to streamline and automate core operations of
                diagnostic labs and clinics. From patient management and doctor
                scheduling to billing and reporting, PULSE empowers healthcare
                professionals with tools that are easy to use and tailored for
                performance and compliance.
              </p>
              <ul className="about-list">
                {[
                  "Simplified reporting workflows",
                  "Accurate billing & invoicing",
                  "Mobile & web-based accessibility",
                  "Scalable from small clinics to large labs",
                ].map((item) => (
                  <li key={item}>
                    <i className="fas fa-check-circle"></i> {item}
                  </li>
                ))}
              </ul>
              <a href="#contact" className="btn btn-primary">
                <i className="fas fa-calendar-check"></i> Request a Demo
              </a>
            </div>
            <div className="about-image">
              <img
                src="/assets_landing_page/dashboard.png"
                alt="About PULSE Dashboard"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════ CONTACT ═══════════════════════ */}
      <section id="contact" className="contact">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info">
              <div className="section-header">
                <h2>Ready to transform your healthcare practice?</h2>
                <p>
                  Get in touch with our team to schedule a personalized demo and
                  see how PULSE can help your clinic thrive.
                </p>
              </div>
              <div className="contact-details">
                {[
                  { icon: "fa-building", text: "Unit Infotech Solutions" },
                  { icon: "fa-envelope", text: "service@elitss.in" },
                  { icon: "fa-phone", text: "+91 91 6908 6908" },
                  { icon: "fa-map-marker-alt", text: "Bengaluru, India" },
                ].map((c) => (
                  <div className="contact-item" key={c.text}>
                    <i className={`fas ${c.icon}`}></i>
                    <p>{c.text}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="contact-form">
              <h3>Request a Demo</h3>
              <form id="demo-form">
                {[
                  {
                    id: "name",
                    label: "Full Name",
                    type: "text",
                    placeholder: "Your Name",
                    required: true,
                  },
                  {
                    id: "email",
                    label: "Email",
                    type: "email",
                    placeholder: "Your Email",
                    required: true,
                  },
                  {
                    id: "phone",
                    label: "Phone",
                    type: "tel",
                    placeholder: "Your Phone Number",
                  },
                  {
                    id: "organization",
                    label: "Organization",
                    type: "text",
                    placeholder: "Your Clinic or Lab Name",
                  },
                ].map((f) => (
                  <div className="form-group" key={f.id}>
                    <label htmlFor={f.id}>{f.label}</label>
                    <input
                      type={f.type}
                      id={f.id}
                      name={f.id}
                      placeholder={f.placeholder}
                      required={f.required}
                    />
                  </div>
                ))}
                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Tell us about your needs"
                  ></textarea>
                </div>
                <button type="submit" className="btn btn-primary btn-full">
                  <i className="fas fa-paper-plane"></i> Request Demo
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════ FOOTER ═══════════════════════ */}
      <footer className="footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-logo">
              <h2>PULSE</h2>
              <p>
                The complete Lab and Clinic Management System for modern
                healthcare facilities.
              </p>
            </div>
            <div className="footer-links">
              <h3>Quick Links</h3>
              <ul>
                {[
                  "features",
                  "about",
                  "testimonials",
                  "pricing",
                  "contact",
                ].map((l) => (
                  <li key={l}>
                    <a href={`#${l}`}>
                      {l.charAt(0).toUpperCase() + l.slice(1)}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="footer-links">
              <h3>Features</h3>
              <ul>
                {[
                  "Patient Management",
                  "Doctor Management",
                  "Billing System",
                  "Lab Reports",
                  "Analytics",
                ].map((f) => (
                  <li key={f}>
                    <a href="#features">{f}</a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="footer-newsletter">
              <h3>Stay Updated</h3>
              <p>
                Subscribe to our newsletter for the latest updates and features.
              </p>
              <form className="newsletter-form">
                <input type="email" placeholder="Your Email" />
                <button type="submit" className="btn btn-primary">
                  Subscribe
                </button>
              </form>
              <div className="social-links">
                {[
                  { label: "Facebook", icon: "fa-facebook-f" },
                  { label: "Twitter", icon: "fa-twitter" },
                  { label: "LinkedIn", icon: "fa-linkedin-in" },
                  { label: "Instagram", icon: "fa-instagram" },
                ].map((s) => (
                  <a href="#" aria-label={s.label} key={s.label}>
                    <i className={`fab ${s.icon}`}></i>
                  </a>
                ))}
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>
              &copy; {new Date().getFullYear()} PULSE · Lab &amp; Clinic
              Management System
            </p>
            <div className="footer-legal">
              <a href="#">Privacy Policy</a>
              <a href="#">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default LandingPage;
